import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { InvestmentInCompaniesModel } from '../../../../../modules/investment/incompanies/investment-incompanies.model';
import { AddFileService } from '../../../../../shared/components/add-file/add-file.service';
import { ConboxDialogComponent } from '../../../../../shared/components/add-file/dialog-conbox.component';
import { LocalDB } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { NotificationService } from '../../../../shared/services/notification.service';
import { EMPTY_GUID } from '../../models/investment-company.model';
import { InvestmentCompanyEntityService } from '../../services/investment-company-entity.service';
import { InvestmentCompanyAccountOption, InvestmentCompanyLookupsFacade } from '../../services/investment-company-lookups.facade';
import { roundAmount } from '../../../../../shared/round-amount';

export interface InvestmentCompanyFormDialogData {
  uid?: string;
}

type InvestmentCompanyFormErrors = Partial<Record<
  'account' | 'investmentValue' | 'investmentDate' | 'investmentDescription' | 'numberOfUnitsOrSharesInvested'
  | 'percentageOfProfitabilityOrInterest' | 'termInMonths' | 'netBookValue', string>>;

const MAX_MONEY = 99999999999999999.99;
const MAX_UNITS = 9999999.99;
const MAX_PERCENT = 999.99;
const MAX_TERM = 255;

@Component({
  selector: 'app-investment-company-form-dialog',
  templateUrl: './investment-company-form-dialog.component.html',
  styleUrls: ['./investment-company-form-dialog.component.scss'],
  standalone: false,
})
export class InvestmentCompanyFormDialogComponent implements OnInit, OnDestroy {
  readonly descriptionMaxLength = 150;

  form: UntypedFormGroup;

  ready = false;
  loadError = false;
  saving = false;
  submitted = false;
  saveError = '';
  errors: InvestmentCompanyFormErrors = {};

  accounts: InvestmentCompanyAccountOption[] = [];

  fileUid: string | null = null;
  isImage = false;
  isPdf = false;
  loadingFile = false;
  imageUrl: string | null = null;
  pdfUrl: SafeResourceUrl | null = null;

  private loaded: InvestmentInCompaniesModel | null = null;
  private objectUrl: string | null = null;
  private readonly destroy$ = new Subject<void>();

  static open(dialog: MatDialog, data: InvestmentCompanyFormDialogData): Observable<boolean> {
    return dialog.open<InvestmentCompanyFormDialogComponent, InvestmentCompanyFormDialogData, boolean>(InvestmentCompanyFormDialogComponent, {
      width: '820px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<InvestmentCompanyFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: InvestmentCompanyFormDialogData,
    private entity: InvestmentCompanyEntityService,
    private lookups: InvestmentCompanyLookupsFacade,
    private addFileService: AddFileService,
    private sanitizer: DomSanitizer,
    private notify: NotificationService,
  ) {
    this.form = this.fb.group({
      account: [null],
      investmentValue: [null],
      investmentDate: [null],
      investmentDescription: [''],
      numberOfUnitsOrSharesInvested: [null],
      percentageOfProfitabilityOrInterest: [null],
      termInMonths: [null],
      netBookValue: [null],
      lastBalanceSheetDate: [null],
      generatesReturns: [false],
    });
  }

  get isEdit(): boolean {
    return !!this.data && !!this.data.uid;
  }

  get title(): string {
    return this.isEdit ? 'Editar inversión en empresas' : 'Nueva inversión en empresas';
  }

  get subtitle(): string {
    if (this.isEdit && this.loaded) {
      return [this.loaded.investmentDescription, this.loaded.accountingModelsNumber].filter(Boolean).join(' · ') || 'Modifica los datos de la inversión';
    }
    return 'Registra una participación, préstamo o crédito a empresas del grupo, asociadas o terceros';
  }

  get accountControl(): UntypedFormControl {
    return this.form.get('account') as UntypedFormControl;
  }

  get investmentDescriptionControl(): UntypedFormControl {
    return this.form.get('investmentDescription') as UntypedFormControl;
  }

  get investmentValueControl(): UntypedFormControl {
    return this.form.get('investmentValue') as UntypedFormControl;
  }

  get investmentDateControl(): UntypedFormControl {
    return this.form.get('investmentDate') as UntypedFormControl;
  }

  get numberOfUnitsControl(): UntypedFormControl {
    return this.form.get('numberOfUnitsOrSharesInvested') as UntypedFormControl;
  }

  get percentageControl(): UntypedFormControl {
    return this.form.get('percentageOfProfitabilityOrInterest') as UntypedFormControl;
  }

  get termInMonthsControl(): UntypedFormControl {
    return this.form.get('termInMonths') as UntypedFormControl;
  }

  get netBookValueControl(): UntypedFormControl {
    return this.form.get('netBookValue') as UntypedFormControl;
  }

  get lastBalanceSheetDateControl(): UntypedFormControl {
    return this.form.get('lastBalanceSheetDate') as UntypedFormControl;
  }

  get generatesReturnsControl(): UntypedFormControl {
    return this.form.get('generatesReturns') as UntypedFormControl;
  }

  get hasFile(): boolean {
    return !!this.fileUid && this.fileUid !== EMPTY_GUID;
  }

  get descriptionLength(): number {
    return String(this.form.get('investmentDescription').value || '').length;
  }

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.submitted) this.errors = this.validate();
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.revokePreview();
    this.destroy$.next();
    this.destroy$.complete();
  }

  retry(): void {
    this.loadError = false;
    this.load();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (!file) return;

    const name = file.name.toLowerCase();
    const pdf = file.type === 'application/pdf' || name.endsWith('.pdf');
    const image = /\.(png|jpe?g)$/.test(name) || file.type === 'image/png' || file.type === 'image/jpeg';
    if (!pdf && !image) {
      this.notify.warning('Solo se admiten archivos PDF, PNG o JPG.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    this.loadingFile = true;
    this.addFileService.attachFile(formData, consts.INVESTMENT_ENTITY)
      .then(response => {
        if (!response || response.error) {
          this.notify.error(response && response.message ? response.message : 'No se pudo subir el archivo.');
          return;
        }
        LocalDB.RemoveForFiles();
        this.fileUid = response.uid;
        this.isPdf = pdf;
        this.isImage = !pdf;
        this.showPreview(file);
      })
      .catch(error => this.notify.error(error && error.error && error.error.message ? error.error.message : 'No se pudo subir el archivo.'))
      .finally(() => this.loadingFile = false);
  }

  pickFromFiles(): void {
    this.dialog.open(ConboxDialogComponent, {
      width: '80%',
      height: '80%',
      maxWidth: '96vw',
      data: { originEntity: consts.INVESTMENT_ENTITY, filePath: consts.INVESTMENT_FILE_PATH }
    }).afterClosed().subscribe(result => {
      if (!result || !result.uid) return;

      const pdf = result.type === consts.TYPE_FILE_PDF;
      const image = result.type === consts.TYPE_FILE_IMAGE;
      if (!pdf && !image) return;

      this.loadingFile = true;
      const request = pdf
        ? this.addFileService.getImageFromFilesPDF(result.uid, consts.INVESTMENT_ENTITY)
        : this.addFileService.getImageFromFiles(result.uid, consts.INVESTMENT_ENTITY);

      request
        .then(blob => {
          this.fileUid = result.uid;
          this.isPdf = pdf;
          this.isImage = image;
          this.showPreview(blob);
        })
        .catch(() => this.notify.error('No se pudo cargar el documento seleccionado.'))
        .finally(() => this.loadingFile = false);
    });
  }

  removeFile(): void {
    this.fileUid = null;
    this.isImage = false;
    this.isPdf = false;
    this.revokePreview();
  }

  save(): void {
    if (this.saving || this.loadingFile) return;
    this.submitted = true;
    this.saveError = '';
    this.errors = this.validate();
    if (Object.keys(this.errors).length) return;

    const payload = this.buildPayload();
    const request = this.isEdit ? this.entity.update(this.data.uid, payload) : this.entity.create(payload);

    this.saving = true;
    this.dialogRef.disableClose = true;
    request
      .then(() => {
        this.notify.ok(this.isEdit ? 'Inversión actualizada correctamente' : 'Inversión creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        const message = error && error.error && typeof error.error.message === 'string' ? error.error.message : '';
        this.saveError = message || (this.isEdit
          ? 'Ha ocurrido un error inesperado al actualizar la inversión'
          : 'Ha ocurrido un error inesperado al crear la inversión');
      });
  }

  close(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private load(): void {
    this.ready = false;
    const record: Promise<InvestmentInCompaniesModel | null> = this.isEdit ? this.entity.get(this.data.uid) : Promise.resolve(null);

    Promise.all([record, this.lookups.accounts()])
      .then(([item, accounts]) => {
        this.accounts = accounts;
        this.loaded = item;
        if (item) this.applyRecord(item);
        this.ready = true;
      })
      .catch(() => {
        this.loadError = true;
        this.ready = true;
      });
  }

  private applyRecord(item: InvestmentInCompaniesModel): void {
    this.form.patchValue({
      account: this.findAccount(item.accountingModelsNumber),
      investmentValue: this.numberOrNull(item.investmentValue),
      investmentDate: this.fromApiDate(item.investmentDate),
      investmentDescription: item.investmentDescription || '',
      numberOfUnitsOrSharesInvested: this.numberOrNull(item.numberOfUnitsOrSharesInvested),
      percentageOfProfitabilityOrInterest: this.numberOrNull(item.percentageOfProfitabilityOrInterest),
      termInMonths: this.numberOrNull(item.termInMonths),
      netBookValue: this.numberOrNull(item.netBookValue),
      lastBalanceSheetDate: this.fromApiDate(item.lastBalanceSheetDate),
      generatesReturns: !!item.generatesReturns,
    }, { emitEvent: false });

    this.fileUid = item.filesInvestmentUid && item.filesInvestmentUid !== EMPTY_GUID ? item.filesInvestmentUid : null;
    this.isImage = !!item.isImage;
    this.isPdf = !!item.isPdf;
    if (this.hasFile && (this.isImage || this.isPdf)) {
      this.loadingFile = true;
      const request = this.isPdf
        ? this.addFileService.getImageFromFilesPDF(this.fileUid, consts.INVESTMENT_ENTITY)
        : this.addFileService.getImageFromFiles(this.fileUid, consts.INVESTMENT_ENTITY);
      request
        .then(blob => this.showPreview(blob))
        .catch(() => this.notify.warning('No se pudo cargar la vista previa del documento.'))
        .finally(() => this.loadingFile = false);
    }
  }

  private findAccount(number: string): InvestmentCompanyAccountOption | null {
    if (!number) return null;
    const existing = this.accounts.find(account => account.number === number);
    if (existing) return existing;
    const fallback: InvestmentCompanyAccountOption = { id: number, display: number, number, name: '', accountingModelsId: 0 };
    this.accounts = [...this.accounts, fallback];
    return fallback;
  }

  private validate(): InvestmentCompanyFormErrors {
    const value = this.form.getRawValue();
    const errors: InvestmentCompanyFormErrors = {};

    if (!value.account || !value.account.number) errors.account = 'Selecciona el tipo de inversión';

    if (this.isBlank(value.investmentValue) || isNaN(Number(value.investmentValue))) {
      errors.investmentValue = 'Indica el valor de la inversión';
    } else if (Number(value.investmentValue) <= 0) {
      errors.investmentValue = 'El valor de la inversión debe ser mayor que 0';
    } else if (Number(value.investmentValue) > MAX_MONEY) {
      errors.investmentValue = 'El valor de la inversión es demasiado alto';
    }

    if (!value.investmentDate) errors.investmentDate = 'Indica la fecha de la inversión';

    const description = String(value.investmentDescription || '').trim();
    if (!description) {
      errors.investmentDescription = 'Indica la descripción';
    } else if (description.length > this.descriptionMaxLength) {
      errors.investmentDescription = `Máximo ${this.descriptionMaxLength} caracteres`;
    }

    const units = this.optionalRange(value.numberOfUnitsOrSharesInvested, 0, MAX_UNITS);
    if (units) errors.numberOfUnitsOrSharesInvested = units;

    const percent = this.optionalRange(value.percentageOfProfitabilityOrInterest, 0, MAX_PERCENT);
    if (percent) errors.percentageOfProfitabilityOrInterest = percent;

    if (!this.isBlank(value.termInMonths)) {
      const term = Number(value.termInMonths);
      if (isNaN(term) || !Number.isInteger(term) || term < 0 || term > MAX_TERM) {
        errors.termInMonths = `Indica un número entero de meses entre 0 y ${MAX_TERM}`;
      }
    }

    const netBook = this.optionalRange(value.netBookValue, -MAX_MONEY, MAX_MONEY);
    if (netBook) errors.netBookValue = netBook;

    return errors;
  }

  private optionalRange(raw: any, min: number, max: number): string | null {
    if (this.isBlank(raw)) return null;
    const value = Number(raw);
    if (isNaN(value)) return 'Importe no válido';
    if (value < min || value > max) return `Debe estar entre ${this.formatNumber(min)} y ${this.formatNumber(max)}`;
    return null;
  }

  private buildPayload(): InvestmentInCompaniesModel {
    const value = this.form.getRawValue();
    const base: any = this.loaded ? JSON.parse(JSON.stringify(this.loaded)) : {};
    const account = value.account as InvestmentCompanyAccountOption;
    const payload: any = {
      ...base,
      accountingModelsNumber: account.number,
      accountingModelsId: account.accountingModelsId || 0,
      investmentValue: this.round(Number(value.investmentValue)),
      investmentDate: this.toApiDate(value.investmentDate),
      investmentDescription: String(value.investmentDescription).trim(),
      numberOfUnitsOrSharesInvested: this.isBlank(value.numberOfUnitsOrSharesInvested) ? 0 : this.round(Number(value.numberOfUnitsOrSharesInvested)),
      percentageOfProfitabilityOrInterest: this.isBlank(value.percentageOfProfitabilityOrInterest) ? 0 : this.round(Number(value.percentageOfProfitabilityOrInterest)),
      termInMonths: this.isBlank(value.termInMonths) ? 0 : Number(value.termInMonths),
      netBookValue: this.isBlank(value.netBookValue) ? 0 : this.round(Number(value.netBookValue)),
      lastBalanceSheetDate: value.lastBalanceSheetDate ? this.toApiDate(value.lastBalanceSheetDate) : null,
      generatesReturns: !!value.generatesReturns,
      filesInvestmentUid: this.hasFile ? this.fileUid : EMPTY_GUID,
      isImage: this.hasFile && this.isImage,
      isPdf: this.hasFile && this.isPdf,
      paided: base.paided || '',
      manualMovement: null,
    };
    return payload;
  }

  private showPreview(blob: Blob): void {
    this.revokePreview();
    if (!blob) return;
    this.objectUrl = URL.createObjectURL(blob);
    if (this.isPdf) {
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
    } else {
      this.imageUrl = this.objectUrl;
    }
  }

  private revokePreview(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.imageUrl = null;
    this.pdfUrl = null;
  }

  private isBlank(value: any): boolean {
    return value === null || value === undefined || String(value).trim() === '';
  }

  private numberOrNull(value: any): number | null {
    return value === null || value === undefined || value === '' || isNaN(Number(value)) ? null : Number(value);
  }

  private round(value: number): number {
    return roundAmount(value);
  }

  private formatNumber(value: number): string {
    return value.toLocaleString('es-ES', { maximumFractionDigits: 2 });
  }

  private fromApiDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) return null;
    return date;
  }

  private toApiDate(value: Date | string): any {
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : formatDate(date, 'yyyy-MM-dd', 'es-ES');
  }
}
